import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Text, IconButton, Skeleton, useMediaQuery, Image } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { getBlogs } from '../../utilities/blogUtils';

const AUTO_SLIDE_MS = 6000;
const ANIMATION_DURATION_MS = 300;

const NewsCarousel = () => {
  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const navigate = useNavigate();

  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");

  useEffect(() => {
    getBlogs(setBlogs, setIsLoading, true);
  }, []);

  const slides = useMemo(() => {
    return (blogs || []).map((post) => {
      const image =
        post?.images?.[0]?.url ||
        post?.thumbnail?.url ||
        post?.image ||
        '';

      const rawDescription =
        post?.contentSnippet ||
        post?.summary ||
        post?.description ||
        post?.content ||
        '';

      return {
        id: post?.id,
        title: post?.title || 'Untitled',
        date: post?.published || post?.updated || '',
        description: String(rawDescription).replace(/<[^>]*>/g, '').trim(),
        image,
        // Blogger post URL (supports common key names)
        url: post?.url || post?.link || post?.alternateLink || '',
      };
    });
  }, [blogs]);

  const total = slides.length;
  const currentSlide = slides[currentSlideIndex];

  const goToNext = useCallback(() => {
    if (!total) return;
    setCurrentSlideIndex((prev) => (prev + 1) % total);
  }, [total]);

  const goToPrev = useCallback(() => {
    if (!total) return;
    setCurrentSlideIndex((prev) => (prev - 1 + total) % total);
  }, [total]);

  const goToSlide = useCallback((idx) => {
    setCurrentSlideIndex(idx);
  }, []);

  // Auto-advance
  useEffect(() => {
    if (!total) return;
    const timer = setInterval(goToNext, AUTO_SLIDE_MS);
    return () => clearInterval(timer);
  }, [currentSlideIndex, goToNext, total]); // currentSlideIndex: resets timer on manual change

  // Clamp index if data changes
  useEffect(() => {
    if (!total) {
      setCurrentSlideIndex(0);
      setCurrentVisibleSlideIndex(0);
      return;
    }
    setCurrentSlideIndex((prev) => (prev >= total ? 0 : prev));
    setCurrentVisibleSlideIndex((prev) => (prev >= total ? 0 : prev));
  }, [total]);

  // fade in and out effect
  const [fadeOpacity, setFadeOpacity] = useState(1);
  const [currentVisibleSlideIndex, setCurrentVisibleSlideIndex] = useState(0);

  useEffect(() => {
    if (!total || currentVisibleSlideIndex === currentSlideIndex) return;

    setFadeOpacity(0); // fades the currently visible slide (separate from current slide)
    const timeout = setTimeout(() => {
      setCurrentVisibleSlideIndex(currentSlideIndex);
      setFadeOpacity(1);
    }, ANIMATION_DURATION_MS);

    return () => clearTimeout(timeout);
  }, [currentSlideIndex, currentVisibleSlideIndex, total]);

  // get the currently visible slide (for before-after slide transitions)
  const visibleSlide = slides[currentVisibleSlideIndex];

  const formattedDate = visibleSlide?.date
    ? new Date(visibleSlide.date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
    : '';

  const handleSlideClick = () => {
    if (!visibleSlide?.url) return;
    navigate("/news/" + visibleSlide.id);
  };

  useEffect(() => {
    if (!total) return;
    if (currentVisibleSlideIndex === currentSlideIndex && fadeOpacity === 0) {
      setFadeOpacity(1);
    }
  }, [currentVisibleSlideIndex, currentSlideIndex, fadeOpacity, total]);


  return (
    <Box w="100%" maxW="100%" overflow="hidden">
      <Box
        position="relative"
        w="100%"
        h={{ base: 'auto', md: '65vh' }}
        minH={{ base: 'auto', md: '380px' }}
        maxH={{ md: '780px' }}
        transition="opacity 0.5s ease"
        opacity={fadeOpacity}
        /* spacning={1} */
        onClick={handleSlideClick}
      >

      {isLoading && (
        <Skeleton position="absolute" inset={0} startColor="gray.700" endColor="gray.600" />
      )}
      
      {isLargerThan768 ? 
        <>
        {/* Main bg image */}
        <Box
          position="absolute"
          //h="50%"
          inset={0}
          zIndex={1}
          bgImage={!isLoading && visibleSlide?.image ? `url("${visibleSlide.image}")` : undefined}
          bgRepeat="no-repeat"
          bgSize={{ base: 'contain', md: 'cover' }}
          bgPosition="center top"
          pointerEvents="none"
          /* border ="2px solid rgba(0, 255, 0, 1)" */
        />

        {/* Content */}
        <Flex
          position="absolute"
          left={{ base: 4, md: 8 }}
          bottom={{ base: 12, md: 16 }}
          padding={{ base: 0, md: 5 }}
          paddingTop={{ base: 10, md: 30 }}
          paddingRight={{ base: 20, md: 50 }}
          left={0}
          bottom={0}
          w="100%"
          direction="column"
          color="white"
          maxW={{ base: '92%', sm: '80%', md: '52%', lg: '42%' }}
          zIndex={2}
          gap={2}
          pointerEvents="none"
          /* border="2px solid rgba(255, 0, 0, 1)" */
          
          /* Text readability Overlay */ 
          _before={{
            content: '""',
            position: "absolute",
            inset: "-18px",
            bg: "rgba(0, 0, 0, 0.60)", //0.35 old
            filter: "blur(24px)",
            borderRadius: "3xl",
            zIndex: -1,
          }}

        >
          <Box bg=""/* POSSIBLE change #2 */ p={4} color="white" borderRadius="md">
            <Text
              fontSize={{ base: '2xl', md: '4xl' }}
              fontWeight="700"
              lineHeight={1.1}
              noOfLines={2}
              paddingBottom={{ base: '1px', md: '5px' }}
            >
              {isLoading ? 'Loading…' : visibleSlide?.title}
            </Text>

            {!!formattedDate && (
              <Text
                fontSize={{ base: '11', md: 'md' }}
                fontWeight="500   "
                opacity={0.9}
              >
                {formattedDate}
              </Text>
            )}

            <Text
              fontSize={{ base: '10', md: 'lg' }}
              lineHeight="1.2"
              opacity={0.95}
              noOfLines={2}
            >
              {isLoading
                ? 'Fetching latest blog content...'
                : visibleSlide?.description?.replace(/&nbsp;/g, ' ').match(/^.*?[.!?]+/)?.[0].trim() || 'Read more in the full article.'}
            </Text>
          </Box>
        </Flex>
      </> 
      /* MOBILE VIEW */
      : (
      <>
        {/* <Box
          position="absolute"
          inset={0}
          zIndex={0}
          bg={isLoading ? 'gray.900' : undefined}
          bgImage={!isLoading && visibleSlide?.image ? `url("${visibleSlide.image}")` : undefined}
          bgSize="auto"
          bgPosition="center"
          bgRepeat="repeat"
          cursor={!isLoading && visibleSlide?.url ? 'pointer' : 'default'}
          
          transform="scale(1.5)"
          filter="blur(10px) saturate(0.1) brightness(0.7)" 
          // bg={"rgba(0, 0, 0, 0)"}
        /> */}
        
        <Box position="relative" w="100%" zIndex={1}>
          {!isLoading && visibleSlide?.image && (
            <Image
              src={visibleSlide.image}
              alt={visibleSlide.title || 'news image'}
              w="100%"
              h="auto"
              objectFit="contain"
              pointerEvents="none"
              maxH={{ base: '80vh', md: '65vh' }}
            />
          )}
        </Box>

        {/* Blog text */}
        <Flex
          position="relative"
          w="100%"
          direction="column"
          color="white"
          /* px={1}
          py={1} */
          zIndex={2}
          /* gap={1} */
          /* border ="2px solid rgba(255, 255, 0, 1)" */
        >
          <Box 
          /* paddingTop={3} */
          /* paddingX={3} */
          color="black" 
          >
            <Box
              paddingY={2}
              paddingX={3} 
              bg={"rgba(28, 85, 150, 1)"}
              /* borderBottomLeftRadius='10px'
              borderBottomRightRadius='10px' */
            > 
              <Text
                fontSize={{ base: '1.25rem' }}
                fontWeight="700"
                lineHeight={1.1}
                noOfLines={2}
                paddingBottom="1px"
                color="white"
              >
                {isLoading ? 'Loading…' : visibleSlide?.title}
              </Text>
            </Box>
            
            <Box
              paddingY={2}
              paddingX={3} 
            > 
              {!!formattedDate && (
                <Text fontSize="0.75rem" opacity={0.9}>{formattedDate}</Text>
              )}

              <Text fontSize="0.75rem" lineHeight="1.2" opacity={0.95} noOfLines={2}>
                {isLoading
                  ? 'Fetching latest blog content...'
                  : visibleSlide?.description?.replace(/&nbsp;/g, ' ').match(/^.*?[.!?]+/)?.[0].trim() || 'Read more in the full article.'}
              </Text>
            </Box>
          </Box>
        </Flex>

        <Flex
          position="relative"
          h="auto"
          w="100%"
          direction="column"
          color="white"
          paddingBottom={3}
          zIndex={2}
          gap={2}
          justify="center"
        > 

        {/* Carousel dots */}
        {!isLoading && total > 1 && (
          <Flex 
            justify="center" 
            gap={2} 
            py={2} 
            onClick={(e) => e.stopPropagation()} 
          >
            {slides.map((slide, idx) => (
              <Box
                key={slide.id || idx}
                as="button"
                aria-label={`Go to slide ${idx + 1}`}
                onClick={() => goToSlide(idx)}
                w={idx === currentSlideIndex ? '16px' : '10px'}
                h="7px"
                borderRadius="full"
                bg={idx === currentSlideIndex ? 'rgba(38, 122, 194, 1)' : 'rgba(38, 122, 194, 0.6)'}
                transition="all 0.25s ease"
                _hover={{ bg: 'whiteAlpha.900' }}
              />
            ))}
          </Flex>
        )}
        </Flex>
      </>
      )}
        {/* Left Control */}
        <IconButton
          aria-label="Previous slide"
          icon={<ChevronLeftIcon boxSize={8} />}
          onClick={(e) => {
            e.stopPropagation();
            goToPrev();
          }}
          position="absolute"
          left={{ base: 2, md: 4 }}
          top="35%"
          transform="translateY(-50%)"
          zIndex={3}
          variant="ghost"
          color="white"
          bg="rgba(0,0,0,0.30)"
          _hover={{ bg: 'rgba(0,0,0,0.48)' }}
          _active={{ bg: 'rgba(0,0,0,0.65)' }}
          borderRadius="full"
          size="lg"
          isDisabled={!total}
        />

        {/* Right Control */}
        <IconButton
          aria-label="Next slide"
          icon={<ChevronRightIcon boxSize={8} />}
          onClick={(e) => {
            e.stopPropagation();
            goToNext();
          }}
          position="absolute"
          right={{ base: 2, md: 4 }}
          top="35%"
          transform="translateY(-50%)"
          zIndex={3}
          variant="ghost"
          color="white"
          bg="rgba(0,0,0,0.30)"
          _hover={{ bg: 'rgba(0,0,0,0.48)' }}
          _active={{ bg: 'rgba(0,0,0,0.65)' }}
          borderRadius="full"
          size="lg"
          isDisabled={!total}
        />

        {/* Dot indicators */}
        {!isLoading && total > 1 && isLargerThan768 && (
          <Flex
            position="absolute"
            left="50%"
            bottom={{ base: 3, md: 5 }}
            transform="translateX(-50%)"
            zIndex={3}
            gap={2}
            onClick={(e) => e.stopPropagation()}
            display={{ base: 'none', md: 'flex' }}
            /* border="2px solid rgba(255, 255, 0, 1)" */
          >
            {slides.map((slide, idx) => (
              <Box
                key={slide.id || idx}
                as="button"
                aria-label={`Go to slide ${idx + 1}`}
                onClick={() => goToSlide(idx)}
                w={idx === currentSlideIndex ? '18px' : '10px'}
                h="10px"
                borderRadius="full"
                bg={idx === currentSlideIndex ? 'white' : 'whiteAlpha.600'}
                transition="all 0.25s ease"
                _hover={{ bg: 'whiteAlpha.900' }}
              />
            ))}
          </Flex>
        )}
      </Box>
    </Box>
  );
};

export default NewsCarousel;
